<template>
  <div v-if="visible" class="limits-modal-overlay" @click.self="onCancel">
    <div class="limits-modal">
      <div class="limits-modal__header">
        <h2 class="limits-modal__title">{{ title }}</h2>
        <button
          class="limits-modal__close-btn"
          @click="onCancel"
          aria-label="Close"
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="15" cy="15" r="15" fill="#DBE7FD" />
            <path
              d="M10 10L20 20"
              stroke="#64748B"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M10 20L20 10"
              stroke="#64748B"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <div class="limits-modal__subtitle">
        {{ subtitle }}
      </div>

      <div v-if="!operationType" class="limits-modal__info">
        {{ $t('limits-modal.info.selectService') }}
      </div>

      <form v-else class="limits-modal__form" @submit.prevent="onSave">
        <div
          v-for="(row, index) in formRows"
          :key="index"
          class="limits-modal__row limits-modal__row--aligned"
        >
          <div class="limits-modal__checkbox-container">
            <label class="limits-modal__checkbox-label">
              <input
                type="checkbox"
                v-model="row.checked"
                class="limits-modal__checkbox"
              />
            </label>
          </div>

          <div class="limits-modal__select-container">
            <CustomSelect
              v-model:value="row.metric"
              :options="limitsOptions"
              valueKey="value"
              :title="$t('limits-modal.select.title')"
              :placeholder="$t('limits-modal.select.placeholder')"
              class="limits-modal__select fixed-select"
            />
          </div>

          <div class="limits-modal__input-container">
            <TextInput
              v-model:value="row.value"
              :title="$t('limits-modal.text-input.title')"
              :placeholder="$t('limits-modal.text-input.placeholder')"
              :numeric="true"
              class="limits-modal__text-input"
            />
          </div>

          <div class="limits-modal__action-container">
            <button
              type="button"
              class="limits-modal__add-row-btn"
              @click="addRow"
              aria-label="Add row"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="23"
                viewBox="0 0 22 23"
                fill="none"
              >
                <circle cx="11" cy="11.25" r="11" fill="#E9F0FF" />
                <path
                  d="M11 5.25C11.2761 5.25003 11.5 5.47388 11.5 5.75V11.1025H16.5L16.6016 11.1123C16.8291 11.1592 17 11.3612 17 11.6025C16.9999 11.8441 16.8285 12.0453 16.6006 12.0918L16.5 12.1025H11.5V16.75C11.5 17.0261 11.2761 17.25 11 17.25C10.7239 17.25 10.5 17.0261 10.5 16.75V12.1025H5.50586C5.22989 12.1025 5.00607 11.8785 5.00586 11.6025C5.00587 11.3264 5.22976 11.1026 5.50586 11.1025H10.5V5.75C10.5 5.47386 10.7239 5.25 11 5.25Z"
                  fill="#465A8B"
                />
              </svg>
            </button>

            <button
              type="button"
              class="limits-modal__add-row-btn"
              @click="removeRow(index)"
              aria-label="Remove row"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <circle cx="11" cy="11" r="11" fill="#E9F0FF" />
                <path
                  d="M16.5244 10.8555C16.7521 10.9022 16.9229 11.1042 16.9229 11.3457C16.9229 11.5872 16.7521 11.7892 16.5244 11.8359L16.4229 11.8457H5.57715C5.30101 11.8457 5.07715 11.6218 5.07715 11.3457C5.07715 11.0696 5.30101 10.8457 5.57715 10.8457H16.4229L16.5244 10.8555Z"
                  fill="#465A8B"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="limits-modal__actions-vertical">
          <primary-button
            :label="$t('limits-modal.actions.save')"
            class="limits-modal__btn-save"
            @click="onSave"
          ></primary-button>
          <primary-button
            :label="$t('limits-modal.actions.cancel')"
            class="limits-modal__btn-cancel"
            @click="onCancel"
          ></primary-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import TextInput from '../TextInput.vue'
import CustomSelect from '../NewCustomSelect.vue'

export default {
  name: 'LimitsModal',
  components: {
    PrimaryButton,
    TextInput,
    CustomSelect,
  },
  props: {
    valueDescription: {
      type: String,
      default: 'Test',
    },
  },
  data() {
    return {
      title: '',
      subtitle: '',
      visible: false,
      formRows: [],
      resolvePromise: null,
      operationType: null,
      partnerId: null,
      availableLimits: [],
    }
  },
  computed: {
    limitsOptions() {
      const locale = this.$i18n.locale === 'ua' ? 'uk' : this.$i18n.locale
      return this.availableLimits.map((item) => ({
        value: item.id,
        name: item.name[locale] ?? item.name.en,
      }))
    },
  },
  methods: {
    async getAvailableLimitsList() {
      const { default: CompanyRequestService } = await import(
        '@admin-js-app/services/company-request/company-request.service'
      )

      this.availableLimits = await CompanyRequestService.getAvailableLimitsList(
        this.operationType,
        this.partnerId
      )
    },
    open(title, subtitle, data = {}, operationType, partnerId) {
      this.title = title
      this.subtitle = subtitle
      this.partnerId = partnerId
      this.operationType = operationType

      const processedRows =
        data.dic_service_operation_type === operationType &&
        Array.isArray(data.rule_limit_items) &&
        data.rule_limit_items.length
          ? data.rule_limit_items.map((item) => ({
              metric: item.rule_limit_id,
              value: item.val,
              checked: item.act,
            }))
          : []

      this.formRows =
        processedRows.length > 0
          ? processedRows
          : [{ metric: '', value: '', checked: true }]

      if (operationType) {
        this.getAvailableLimitsList()
      }

      this.visible = true
      return new Promise((resolve) => {
        this.resolvePromise = resolve
        this.$nextTick(() => {
          const firstInput = this.$el.querySelector('.limits-modal__select')
          if (firstInput) firstInput.focus()
        })
      })
    },
    onSave() {
      const result = this.formRows.map((row) => {
        const opt = this.limitsOptions.find((o) => o.value === row.metric) || {}
        return {
          rule_limit_id: row.metric,
          name: opt.name || '',
          val: Number(row.value),
          act: row.checked,
        }
      })
      this.resolvePromise(result)
      this.visible = false
    },
    onCancel() {
      this.resolvePromise(null)
      this.visible = false
    },
    addRow() {
      this.formRows.push({ metric: '', value: '', checked: true })
    },
    removeRow(index) {
      this.formRows.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.limits-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.limits-modal {
  width: 90%;
  max-width: 700px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  border-radius: 22px;
  background: #fff;
}
.limits-modal__header {
  position: relative;
  margin-bottom: 12px;
  text-align: center;
  padding: 0px 60px;
}
.limits-modal__title {
  font-family: Montserrat;
  font-size: 16px;
  font-weight: 600;
  color: var(--text2, #666d79);
  margin: 0;
  display: inline-block;
}
.limits-modal__close-btn {
  position: absolute;
  top: -25px;
  right: 0px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.limits-modal__subtitle {
  color: var(--H3, #67768a);
  text-align: center;
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 0 0 20px;
}
.limits-modal__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 10px;
}
.limits-modal__row--aligned {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.limits-modal__checkbox-container {
  flex: 0 0 auto;
}
.limits-modal__checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #64748B;
}
.limits-modal__checkbox::-ms-check {
  background: #64748B;
  border-color: #64748B;
}
.limits-modal__checkbox-label {
  display: flex;
  align-items: center;
}
.limits-modal__select-container {
  flex: 1 1 0;
}
.limits-modal__select .custom-select__header {
  height: 56px;
  padding-top: 0;
  border: 1px solid #e0e6f2;
  border-radius: 6px;
  background-color: transparent;
}
.limits-modal__select .custom-select--big .custom-select__header {
  height: 56px;
}
.limits-modal__input-container {
  flex: 1 1 0;
}
.limits-modal__text-input .input__el {
  height: 56px;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 56px;
  border: 1px solid #e0e6f2;
  border-radius: 6px;
}
.limits-modal__action-container {
  display: flex;
  align-items: center;
  gap: 8px;
}
.limits-modal__add-row-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.limits-modal__add-row-btn svg {
  display: block;
}
.limits-modal__actions-vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.limits-modal__btn-cancel {
  width: 100%;
  max-width: 200px;
  border-radius: 8px;
  background: #fff;
  color: var(--Main, #64748B);
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  padding: 20px 10px;
}
.limits-modal__btn-save {
  width: 100%;
  max-width: 200px;
  border-radius: 8px;
  background: #64748B;
  color: #fff;
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  border: none;
  padding: 20px 10px;
}
.fixed-select {
  width: 250px;
}
.fixed-select .custom-select__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.limits-modal__info {
  margin: 24px 0;
  padding: 16px;
  text-align: center;
  font-style: italic;
  color: #4a5568;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
</style>
